import { Translate2 } from 'common'
import ListServiceProvider from '../../services/ListServiceProvider'

let cachedNames: Promise<string[]> | undefined

/**
 * Returns the names of every list the user has flagged as a stock list.
 *
 * Prefer per-part `stockedLists` from verifyStockedAndTaggedParts when available.
 * This helper is a fallback that resolves names from GET /list. The result is
 * cached for the session and must be invalidated whenever a list is created
 * or updated.
 *
 * @returns The stock list names, or an empty array when the request fails.
 */
export const getStockListNames = async (): Promise<string[]> => {
  cachedNames ??= ListServiceProvider.getCategoriesAndLists()
    .then(({ categories }) =>
      categories.flatMap((category) =>
        (category.lists || [])
          .filter((list) => list.isStock)
          .map((list) => list.name)
      )
    )
    .catch(() => [])

  return cachedNames
}

/**
 * Clears the cached stock list names so the next lookup refetches them.
 */
export const invalidateStockListNames = (): void => {
  cachedNames = undefined
}

/**
 * Builds the tooltip copy shown on the quantity counter of a stocked part.
 *
 * One list interpolates the name. Several lists join the names. None falls
 * back to the generic stock-list message.
 *
 * @param names The stock list names for this part.
 * @returns The translated tooltip message.
 */
export const buildStockListLabel = (names: string[]): string => {
  if (names.length === 1) return Translate2('itemIsOnYourList', [names[0]])
  if (names.length > 1) {
    return Translate2('itemIsOnLists', [names.join(', ')])
  }
  return Translate2('itemIsOnYourStockList')
}
